import {
  find,
  flatten,
  isEmpty,
  isObject,
  map,
  toLower,
  uniq,
  values,
} from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReceiptValidationType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { formatUtils } from 'utils';

import { ValidationItem } from './types';

type Props = {
  templateValidations: ValidationItem[];
  stopScamValidations: ValidationItem[];
  techProtectionValidations: ValidationItem[];
  chequeWalesValidations: ValidationItem[];
};

export const ReceiptsValidationSummary: React.FC<Props> = ({
  templateValidations,
  stopScamValidations,
  techProtectionValidations,
  chequeWalesValidations,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.receipt_validation',
  });

  const techProtectionVerdictItems = useMemo(() => {
    const verdictValidation = find(techProtectionValidations, {
      type: ReceiptValidationType.TechProtectionVerdict,
    });
    if (verdictValidation && isObject(verdictValidation.details)) {
      return flatten(values(verdictValidation.details)) as {
        type: string;
        expected: string[];
        got: string[];
      }[];
    }
    return [];
  }, [techProtectionValidations]);

  const stopScamValidDetailsTypes = useMemo(() => {
    const stopScamValidResult = find(stopScamValidations, {
      type: ReceiptValidationType.StopScamValid,
    });
    if (isObject(stopScamValidResult?.details)) {
      return uniq(
        map(flatten(values(stopScamValidResult?.details)), (details: any) =>
          toLower(details?.type),
        ),
      );
    }
    return [];
  }, [stopScamValidations]);

  const chequeWalesVerdicts = useMemo(() => {
    const verdictResult = find(chequeWalesValidations, {
      type: ReceiptValidationType.ChequeWalesVerdict,
    });
    if (isObject(verdictResult?.details)) {
      return uniq(
        map(values(verdictResult?.details), (verdict: string) =>
          toLower(verdict),
        ),
      ).filter((verdict) => verdict !== 'original');
    }
    return [];
  }, [chequeWalesValidations]);

  const renderValidationRow = useCallback(
    (validation: ValidationItem) => {
      if (validation.type === ReceiptValidationType.TechProtectionRiskScore) {
        const score = isObject(validation.details)
          ? values(validation.details)[0]
          : null;
        return (
          <div key={validation.type}>
            {`${t(`type.${validation.type}`)}: ${score ?? '—'}`}
          </div>
        );
      }
      return (
        <div key={validation.type}>
          {`${t(`type.${validation.type}`, {
            defaultValue: validation.type,
          })}: ${formatUtils.formatBoolean(validation.success)}`}
        </div>
      );
    },
    [t],
  );

  return (
    <Fragment>
      {!isEmpty(templateValidations) && (
        <div className="tw-text-xs tw-mb-2">
          <div className="tw-font-bold">{t('template_title')}</div>
          {map(templateValidations, renderValidationRow)}
        </div>
      )}
      {!isEmpty(stopScamValidations) && (
        <div className="tw-text-xs tw-mb-2">
          <div className="tw-font-bold">{t('stop_scam_title')}</div>
          {map(stopScamValidations, renderValidationRow)}
          {map(stopScamValidDetailsTypes, (type: string) => (
            <div key={type}>{`- ${t(`stop_scam_verdict.${type}` as any)}`}</div>
          ))}
        </div>
      )}
      {!isEmpty(techProtectionValidations) && (
        <div className="tw-text-xs tw-mt-2 tw-mb-2">
          <div className="tw-font-bold">{t('tech_protection_title')}</div>
          {map(techProtectionValidations, renderValidationRow)}
          {map(techProtectionVerdictItems, (item) => (
            <div key={item.type}>{`- ${t(
              `tech_protection_verdict.${toLower(item.type)}` as any,
              { defaultValue: item.type },
            )}${
              item.expected?.length ? `: ${item.expected.join(', ')}` : ''
            }`}</div>
          ))}
        </div>
      )}
      {!isEmpty(chequeWalesValidations) && (
        <div className="tw-text-xs tw-mt-2 tw-mb-2">
          <div className="tw-font-bold">{t('cheque_wales_title')}</div>
          {map(chequeWalesValidations, renderValidationRow)}
          {map(chequeWalesVerdicts, (verdict: string) => (
            <div key={verdict}>{`- ${t(
              `cheque_wales_verdict.${verdict}` as any,
              { defaultValue: verdict },
            )}`}</div>
          ))}
        </div>
      )}
    </Fragment>
  );
};
